import React, { Suspense, useState, useEffect, useCallback } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from './hooks/queries/queryClient';
import { onboarding } from './api';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { TourProvider } from './contexts/TourContext';
import { Toaster, toast } from 'sonner';
import ErrorBoundary from './components/ErrorBoundary';
import RouteTitle from './components/RouteTitle';
import Sidebar from './components/Sidebar';
import CommandPalette from './components/CommandPalette';
import KeyboardShortcuts from './components/KeyboardShortcuts';
import Breadcrumbs from './components/Breadcrumbs';
import ProductTour from './components/ProductTour';
import useKeyboardShortcuts from './hooks/useKeyboardShortcuts';

// Route-level code splitting — lazy-load all pages
const OnboardingWizard = React.lazy(() => import('./pages/OnboardingWizard'));
const Dashboard = React.lazy(() => import('./pages/Dashboard'));
const Projects = React.lazy(() => import('./pages/Projects'));
const ProjectDetail = React.lazy(() => import('./pages/ProjectDetail'));
const People = React.lazy(() => import('./pages/People'));
const PersonDetail = React.lazy(() => import('./pages/PersonDetail'));
const Templates = React.lazy(() => import('./pages/Templates'));
const ClientTemplates = React.lazy(() => import('./pages/ClientTemplates'));
const Proposals = React.lazy(() => import('./pages/Proposals'));
const NotFound = React.lazy(() => import('./pages/NotFound'));
const ProposalBuilder = React.lazy(() => import('./pages/ProposalBuilder'));
const FeeEstimator = React.lazy(() => import('./pages/FeeEstimator'));
const Analytics = React.lazy(() => import('./pages/Analytics'));
const Settings = React.lazy(() => import('./pages/Settings'));
const AIAssistant = React.lazy(() => import('./components/AIAssistant'));
const FeedbackWidget = React.lazy(() => import('./components/FeedbackWidget'));
const RFPParser = React.lazy(() => import('./pages/RFPParser'));
const GoNoGo = React.lazy(() => import('./pages/GoNoGo'));
const NZGovtWizard = React.lazy(() => import('./pages/NZGovtWizard'));
const AIUsageMonitor = React.lazy(() => import('./pages/AIUsageMonitor'));
const SMEForm = React.lazy(() => import('./pages/SMEForm'));
const Achievements = React.lazy(() => import('./pages/Achievements'));
// Phase 2
const ContentLibrary = React.lazy(() => import('./pages/ContentLibrary'));
const CapabilityStatement = React.lazy(() => import('./pages/CapabilityStatement'));
const ComplianceMatrix = React.lazy(() => import('./pages/ComplianceMatrix'));
// Phase 3
const Login = React.lazy(() => import('./pages/Login'));
const Register = React.lazy(() => import('./pages/Register'));
const ForgotPassword = React.lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = React.lazy(() => import('./pages/ResetPassword'));
const AcceptInvite = React.lazy(() => import('./pages/AcceptInvite'));
// Phase 4
const TenderMonitor = React.lazy(() => import('./pages/TenderMonitor'));
const Integrations = React.lazy(() => import('./pages/Integrations'));
// Phase 5
const Pricing = React.lazy(() => import('./pages/Pricing'));
// Phase 6
const AdvancedAnalytics = React.lazy(() => import('./pages/AdvancedAnalytics'));
const Certifications = React.lazy(() => import('./pages/Certifications'));
const Competitors = React.lazy(() => import('./pages/Competitors'));
const Lessons = React.lazy(() => import('./pages/Lessons'));
// Phase 7
const APISettings = React.lazy(() => import('./pages/APISettings'));
const Branding = React.lazy(() => import('./pages/Branding'));
// F1 — Interactive Web Proposals
const WebProposal = React.lazy(() => import('./pages/WebProposal'));
// Billing & Intelligence
const Billing = React.lazy(() => import('./pages/Billing'));
const Intelligence = React.lazy(() => import('./pages/Intelligence'));
const ImpactReport = React.lazy(() => import('./pages/ImpactReport'));
const ClientIntelligence = React.lazy(() => import('./pages/Clients'));
const AuditLog = React.lazy(() => import('./pages/AuditLog'));
const DecisionLog = React.lazy(() => import('./pages/DecisionLog'));
// F351 — Pursuit calendar (Phase 3)
const PursuitCalendar = React.lazy(() => import('./pages/PursuitCalendar'));
// F13 — Resource Capacity Planning
const ResourcePlanner = React.lazy(() => import('./pages/ResourcePlanner'));
const TenderSettings = React.lazy(() => import('./pages/TenderSettings'));
const Help = React.lazy(() => import('./pages/Help'));

function PrivateRoute({ element }) {
  const { isAuthenticated, loading } = useAuth();
  if (loading) return <div className="loading" style={{ minHeight: '100vh' }}><div className="spinner" /></div>;
  return isAuthenticated ? element : <Navigate to="/login" replace />;
}

function PublicRoute({ element }) {
  const { isAuthenticated, loading } = useAuth();
  if (loading) return <div className="loading" style={{ minHeight: '100vh' }}><div className="spinner" /></div>;
  return isAuthenticated ? <Navigate to="/dashboard" replace /> : element;
}

function AppShell({ onboardingCompleted, setOnboardingCompleted }) {
  const [commandPaletteOpen, setCommandPaletteOpen] = useState(false);
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  // Used by the AppShell ErrorBoundary's resetKeys — when the route changes,
  // any stuck error state from a transient failure (e.g. brief 502 during a
  // deploy) automatically clears so the user doesn't have to refresh.
  const location = useLocation();

  const toggleCommandPalette = useCallback(() => {
    setCommandPaletteOpen(prev => !prev);
  }, []);

  const toggleShortcuts = useCallback(() => {
    setShortcutsOpen(prev => !prev);
  }, []);

  const openHelp = useCallback(() => {
    window.dispatchEvent(new CustomEvent('tenderwright:open-help'));
  }, []);

  useKeyboardShortcuts({
    onCommandPalette: toggleCommandPalette,
    onShowShortcuts: toggleShortcuts,
    onOpenHelp: openHelp,
  });

  // Global toast bridge — useApiMutation with surfaceErrors:true (and any
  // other component) can dispatch a `tenderwright:toast` CustomEvent and
  // it'll be shown via Sonner without that caller needing to import the
  // toast hook itself. Especially useful for hooks/services that run
  // outside React component context.
  useEffect(() => {
    const handler = (e) => {
      const detail = e.detail || {};
      const msg = detail.message || 'Something happened';
      const type = detail.type || 'info';
      if (type === 'success') toast.success(msg);
      else if (type === 'error') toast.error(msg, { duration: 4500 });
      else if (type === 'warning') toast.warning ? toast.warning(msg) : toast(msg);
      else toast(msg);
    };
    window.addEventListener('tenderwright:toast', handler);
    return () => window.removeEventListener('tenderwright:toast', handler);
  }, []);

  return (
    <>
      <CommandPalette isOpen={commandPaletteOpen} onClose={() => setCommandPaletteOpen(false)} />
      <KeyboardShortcuts isOpen={shortcutsOpen} onClose={() => setShortcutsOpen(false)} />
      <RouteTitle />
      <Suspense fallback={<div className="loading"><div className="spinner" /></div>}>
      <Routes>
        {/* Standalone routes — no sidebar */}
        <Route path="/p/:token" element={<WebProposal />} />
        <Route path="/sme/:id" element={<SMEForm />} />
        <Route path="/login" element={<PublicRoute element={<Login />} />} />
        <Route path="/register" element={<PublicRoute element={<Register />} />} />
        <Route path="/forgot-password" element={<PublicRoute element={<ForgotPassword />} />} />
        <Route path="/reset-password" element={<PublicRoute element={<ResetPassword />} />} />
        <Route path="/accept-invite" element={<PublicRoute element={<AcceptInvite />} />} />
        {/* Pricing is public-facing — prospects need to see costs before signing up.
            CTAs route to /register (free) or /billing (paid). The /billing CTA is
            still PrivateRoute-gated, which gives the right "log in to upgrade" UX. */}
        <Route path="/pricing" element={<Pricing />} />

        <Route
          path="*"
          element={
            <PrivateRoute element={
            !onboardingCompleted ? (
              <Routes>
                <Route
                  path="*"
                  element={
                    <OnboardingWizard
                      onComplete={() => setOnboardingCompleted(true)}
                    />
                  }
                />
              </Routes>
            ) : (
              <ErrorBoundary resetKeys={[location.pathname]}>
              <TourProvider>
              <div className="app-layout">
                <Sidebar />
                <ProductTour />
                <main className="main-content">
                  <Breadcrumbs />
                  <Suspense fallback={<div className="loading"><div className="spinner" /></div>}>
                  <Routes>
                    <Route path="/" element={<Navigate to="/dashboard" replace />} />
                    <Route path="/dashboard" element={<Dashboard />} />
                    <Route path="/projects" element={<Projects />} />
                    <Route path="/projects/:id" element={<ProjectDetail />} />
                    <Route path="/people" element={<People />} />
                    <Route path="/people/:id" element={<PersonDetail />} />
                    <Route path="/templates" element={<Templates />} />
                    <Route path="/client-templates" element={<ClientTemplates />} />
                    <Route path="/proposals" element={<Proposals />} />
                    <Route path="/proposals/new" element={<Navigate to="/proposals" replace />} />
                    <Route path="/proposals/:id/build" element={<ProposalBuilder />} />
                    <Route path="/proposals/:id/compliance-matrix" element={<ComplianceMatrix />} />
                    <Route path="/proposals/:id" element={<Navigate to="build" replace />} />
                    <Route path="/fee-estimator" element={<FeeEstimator />} />
                    <Route path="/analytics" element={<Analytics />} />
                    <Route path="/achievements" element={<Achievements />} />
                    <Route path="/rfp-parser" element={<RFPParser />} />
                    <Route path="/go-no-go" element={<GoNoGo />} />
                    <Route path="/ai-usage" element={<AIUsageMonitor />} />
                    <Route path="/nz-govt-wizard" element={<NZGovtWizard />} />
                    <Route path="/settings" element={<Settings />} />
                    {/* Phase 2 */}
                    <Route path="/content-library" element={<ContentLibrary />} />
                    <Route path="/capability-statement" element={<CapabilityStatement />} />
                    {/* Phase 4 */}
                    <Route path="/tenders" element={<TenderMonitor />} />
                    <Route path="/integrations" element={<Integrations />} />
                    {/* Phase 5 */}
                    {/* /pricing is the public route above; logged-in users still
                        hit that one (no sidebar, marketing layout). */}
                    <Route path="/billing" element={<Billing />} />
                    <Route path="/intelligence" element={<Intelligence />} />
                    <Route path="/impact" element={<ImpactReport />} />
                    <Route path="/clients" element={<ClientIntelligence />} />
                    <Route path="/audit-log" element={<AuditLog />} />
                    <Route path="/decisions" element={<DecisionLog />} />
                    <Route path="/calendar" element={<PursuitCalendar />} />
                    {/* Phase 6 */}
                    <Route path="/advanced-analytics" element={<AdvancedAnalytics />} />
                    <Route path="/certifications" element={<Certifications />} />
                    <Route path="/competitors" element={<Competitors />} />
                    <Route path="/lessons" element={<Lessons />} />
                    {/* F13 — Resource Planner */}
                    <Route path="/resource-planner" element={<ResourcePlanner />} />
                    {/* Phase 7 */}
                    <Route path="/settings/api" element={<APISettings />} />
                    <Route path="/settings/branding" element={<Branding />} />
                    <Route path="/settings/tender-sources" element={<TenderSettings />} />
                    {/* In-page tab deep-links (rate-cards, team, profile, org).
                        Sits AFTER the specific routes above so /settings/api etc.
                        still hit their dedicated components. NAL audit B30 —
                        previously /settings/rate-cards 404'd to /dashboard. */}
                    <Route path="/settings/:tab" element={<Settings />} />
                    {/* Convenience aliases — sidebar labels and external bookmarks
                        commonly drop the /settings prefix. Without these, paths
                        like /branding silently catchall-redirected to /dashboard. */}
                    <Route path="/branding" element={<Navigate to="/settings/branding" replace />} />
                    <Route path="/api-keys" element={<Navigate to="/settings/api" replace />} />
                    <Route path="/webhooks" element={<Navigate to="/settings/api" replace />} />
                    <Route path="/tender-sources" element={<Navigate to="/settings/tender-sources" replace />} />
                    <Route path="/audit-trail" element={<Navigate to="/audit-log" replace />} />
                    <Route path="/help" element={<Help />} />
                    <Route path="/docs" element={<Navigate to="/help" replace />} />
                    <Route path="/support" element={<Navigate to="/help" replace />} />
                    {/* B1: real 404 instead of silently redirecting unknown
                        URLs to /dashboard (which masked typo'd/stale links). */}
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                  </Suspense>
                </main>
                <AIAssistant />
                <FeedbackWidget />
              </div>
              </TourProvider>
              </ErrorBoundary>
            )
          } />}
        />
      </Routes>
      </Suspense>
    </>
  );
}

function AuthenticatedApp() {
  const { isAuthenticated, loading: authLoading } = useAuth();
  const [onboardingCompleted, setOnboardingCompleted] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) {
      // Not logged in — skip onboarding check, assume complete so login page renders
      setOnboardingCompleted(true);
      setLoading(false);
      return;
    }
    const checkOnboarding = async () => {
      try {
        const res = await onboarding.getStatus();
        setOnboardingCompleted(res.data.completed);
      } catch (err) {
        // Only show onboarding if we got a valid response saying it's not complete.
        // On auth failure (401/403) or network error, assume onboarding is done
        // so users aren't trapped in the wizard after session expiry.
        const status = err?.response?.status;
        if (status === 401 || status === 403 || !err?.response) {
          setOnboardingCompleted(true);
        } else {
          setOnboardingCompleted(false);
        }
      } finally {
        setLoading(false);
      }
    };
    checkOnboarding();
  }, [authLoading, isAuthenticated]);

  if (authLoading || loading) {
    return (
      <div className="loading" style={{ minHeight: '100vh' }}>
        <div className="spinner" />
      </div>
    );
  }

  return <AppShell onboardingCompleted={onboardingCompleted} setOnboardingCompleted={setOnboardingCompleted} />;
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
    <Toaster position="top-right" richColors closeButton visibleToasts={2} duration={3000} />
    <AuthProvider>
      <BrowserRouter>
        <AuthenticatedApp />
      </BrowserRouter>
    </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
